import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Dumbbell, CheckCircle, XCircle, AlertCircle,
  Settings, Delete, QrCode, Phone, ArrowLeft, Lock,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import jsQR from 'jsqr';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';

const STORAGE_KEY = 'kiosk_gym_code';
const RESET_DELAY = 4000;
const SCAN_INTERVAL = 80;

// ── 날씨 유틸 ──────────────────────────────────────────────
const WEATHER_CACHE_KEY = 'kiosk_weather_cache';
const WEATHER_TTL = 60 * 60 * 1000; // 60분

const WMO_MAP = {
  0: '맑음 ☀️', 1: '대체로 맑음 🌤', 2: '구름 조금 ⛅', 3: '흐림 ☁️',
  45: '안개 🌫', 48: '안개 🌫',
  51: '이슬비 🌦', 53: '이슬비 🌦', 55: '이슬비 🌦',
  61: '비 🌧', 63: '비 🌧', 65: '강한 비 🌧',
  71: '눈 ❄️', 73: '눈 ❄️', 75: '폭설 ❄️',
  80: '소나기 🌦', 81: '소나기 🌦', 82: '강한 소나기 ⛈',
  95: '뇌우 ⛈', 99: '뇌우 ⛈',
};

function getWeatherDesc(code) {
  const keys = Object.keys(WMO_MAP).map(Number).sort((a, b) => a - b);
  let match = keys[0];
  for (const k of keys) { if (code >= k) match = k; else break; }
  return WMO_MAP[match] || '날씨 확인 중';
}

async function loadWeather() {
  try {
    const cached = JSON.parse(localStorage.getItem(WEATHER_CACHE_KEY) || 'null');
    if (cached && Date.now() - cached.ts < WEATHER_TTL) return cached.data;

    const { lat, lon } = await new Promise(resolve => {
      if (!navigator.geolocation) { resolve({ lat: 37.5665, lon: 126.9780 }); return; }
      navigator.geolocation.getCurrentPosition(
        p => resolve({ lat: p.coords.latitude, lon: p.coords.longitude }),
        () => resolve({ lat: 37.5665, lon: 126.9780 }),
        { timeout: 5000 }
      );
    });

    const res = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weathercode&timezone=Asia/Seoul`
    );
    const json = await res.json();
    const data = {
      temp: Math.round(json.current.temperature_2m),
      desc: getWeatherDesc(json.current.weathercode),
    };
    localStorage.setItem(WEATHER_CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
    return data;
  } catch { return null; }
}

// ── 메시지 조립 ──────────────────────────────────────────────
function getSeasonGreeting() {
  const now = new Date();
  const m = now.getMonth() + 1;
  const d = now.getDate();
  if (m === 12 && d >= 24 && d <= 26) return '메리 크리스마스';
  if ((m === 12 && d >= 31) || (m === 1 && d <= 3)) return '새해 복 많이 받으세요';
  if (m === 1 && d >= 20 && d <= 31) return '설 명절 잘 보내세요';
  if (m === 2 && d <= 5) return '설 명절 잘 보내세요';
  if ((m === 9 && d >= 25) || (m === 10 && d <= 5)) return '추석 연휴 즐겁게 보내세요';
  return null;
}

const CHEERS = ['오늘도 파이팅!', '건강한 하루 보내세요!', '최선을 다해봐요!', '오늘도 힘내세요!', '꾸준함이 최고예요!'];

// 화면 표시용 메시지 (날씨 포함)
function buildCheckinMessage(name, weather, cheer) {
  const season = getSeasonGreeting();
  if (season) return `${season}! ${cheer}`;
  const weatherStr = weather ? `${weather.desc} ${weather.temp}°C · ` : '';
  return `${weatherStr}${cheer}`;
}

// 음성 안내용 메시지 (짧고 자연스럽게)
function buildSpeechMessage(name, cheer) {
  const season = getSeasonGreeting();
  if (season) return `${name}님, ${season}!`;
  return `${name}님, 어서오세요. ${cheer}`;
}

// ── 출석왕 마스킹 ──────────────────────────────────────────────
function maskName(name) {
  if (!name) return '';
  if (name.length === 1) return name;
  if (name.length === 2) return name[0] + 'O';
  return name[0] + 'O'.repeat(name.length - 2) + name[name.length - 1];
}
function maskPhone(phone) { return phone ? phone.slice(-4) : '****'; }

function speak(text) {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();

  function doSpeak() {
    const voices = window.speechSynthesis.getVoices();
    const koVoices = voices.filter(v => v.lang.startsWith('ko'));
    // 우선순위: 자연스러운 음성 키워드 → 여성 음성 → 한국어 아무거나
    const best = koVoices.find(v => /yuna|heami|유나|지민|서현/i.test(v.name))
      || koVoices.find(v => /여성|female|woman/i.test(v.name))
      || koVoices[0];

    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'ko-KR';
    u.rate = 0.92;
    u.pitch = 1.05;
    if (best) u.voice = best;
    window.speechSynthesis.speak(u);
  }

  // 음성 목록이 아직 로드 안 됐을 경우 대비
  if (window.speechSynthesis.getVoices().length > 0) {
    doSpeak();
  } else {
    window.speechSynthesis.onvoiceschanged = () => { doSpeak(); window.speechSynthesis.onvoiceschanged = null; };
  }
}

// ── 설정 화면 ──────────────────────────────────────────────
function SetupScreen({ onSetup }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    setError('');
    const { data } = await supabase.rpc('kiosk_get_gym_name', { p_gym_code: code.trim().toUpperCase() });
    if (!data) { setError('헬스장 코드를 찾을 수 없습니다.'); setLoading(false); return; }
    onSetup(code.trim().toUpperCase(), data);
  }

  return (
    <div className="fixed inset-0 bg-gray-900 flex items-center justify-center">
      <div className="bg-gray-800 rounded-2xl p-10 w-full max-w-sm shadow-2xl border border-gray-700">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center">
            <Dumbbell className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="font-bold text-white text-lg">GYM Manager</p>
            <p className="text-xs text-gray-400">키오스크 설정</p>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">헬스장 매장 코드</label>
            <input
              className="w-full bg-gray-700 text-white text-center text-2xl font-mono tracking-widest px-4 py-4 rounded-xl border border-gray-600 focus:border-primary-500 outline-none uppercase"
              placeholder="XXXXXX"
              value={code}
              onChange={e => setCode(e.target.value.toUpperCase())}
              maxLength={8}
              autoFocus
            />
            {error && <p className="text-red-400 text-sm mt-2 text-center">{error}</p>}
          </div>
          <button
            type="submit"
            disabled={loading || !code.trim()}
            className="w-full py-3 bg-primary-600 text-white font-bold rounded-xl hover:bg-primary-700 transition-colors disabled:opacity-50 text-lg"
          >
            {loading ? '확인 중...' : '키오스크 시작'}
          </button>
        </form>
        <p className="text-xs text-gray-500 text-center mt-4">매장 코드는 대시보드 → 설정에서 확인하세요.</p>
      </div>
    </div>
  );
}

// ── 숫자 패드 ──────────────────────────────────────────────
function NumPad({ value, onChange }) {
  function press(n) { if (value.length < 8) onChange(value + n); }
  function del() { onChange(value.slice(0, -1)); }

  // onPointerDown → 터치 디바이스에서 ~300ms onClick 지연 제거
  function tapHandler(fn) {
    return {
      onPointerDown(e) { e.preventDefault(); fn(); },
      onClick(e) { e.preventDefault(); }, // fallback 차단
    };
  }

  const display = (() => {
    const p1 = value.slice(0, 4);
    const p2 = value.slice(4, 8);
    return (
      <span>
        <span className="text-gray-500">010</span>
        <span className="text-gray-500 mx-1">-</span>
        <span className={p1.length === 4 ? 'text-primary-300' : p1.length > 0 ? 'text-white' : 'text-gray-600'}>
          {p1.padEnd(4, '·')}
        </span>
        <span className="text-gray-500 mx-1">-</span>
        <span className={p2.length > 0 ? 'text-primary-300' : 'text-gray-600'}>
          {p2.padEnd(4, '·')}
        </span>
      </span>
    );
  })();

  const btnClass = "bg-gray-800 hover:bg-gray-700 active:bg-gray-600 border border-gray-700 text-white text-2xl font-bold rounded-2xl transition-colors flex items-center justify-center touch-manipulation";

  return (
    <div className="flex flex-col h-full">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl px-6 py-3 text-center font-mono text-2xl font-bold tracking-widest flex items-center justify-center flex-shrink-0">
        {display}
      </div>
      <div className="grid grid-cols-3 grid-rows-4 gap-2 flex-1 mt-3">
        {[1,2,3,4,5,6,7,8,9].map(n => (
          <button key={n} {...tapHandler(() => press(String(n)))} className={btnClass}>
            {n}
          </button>
        ))}
        <div />
        <button {...tapHandler(() => press('0'))} className={btnClass}>
          0
        </button>
        <button
          {...tapHandler(del)}
          className="bg-gray-700 hover:bg-gray-600 active:bg-gray-500 border border-gray-600 text-white rounded-2xl transition-colors flex items-center justify-center touch-manipulation"
        >
          <Delete className="w-6 h-6" />
        </button>
      </div>
    </div>
  );
}

// ── PIN 모달 ────────────────────────────────────────────────
function PinModal({ gymCode, onSuccess, onCancel }) {
  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function press(n) { if (!loading && pin.length < 4) setPin(p => p + n); }
  function del() { if (!loading) { setPin(p => p.slice(0, -1)); setError(''); } }
  function tapHandler(fn) {
    return {
      onPointerDown(e) { e.preventDefault(); fn(); },
      onClick(e) { e.preventDefault(); },
    };
  }

  useEffect(() => {
    if (pin.length !== 4) return;
    setLoading(true);
    supabase.rpc('kiosk_validate_pin', { p_gym_code: gymCode, p_pin: pin })
      .then(({ data, error: rpcError }) => {
        if (data === true || data === null || rpcError) {
          // data === true: PIN 일치, data === null: PIN 미설정, rpcError: RPC 없음 → 모두 허용
          onSuccess();
        } else {
          setShake(true);
          setError('PIN이 올바르지 않습니다.');
          setTimeout(() => { setPin(''); setShake(false); setLoading(false); }, 600);
        }
      });
  }, [pin]); // eslint-disable-line

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
      <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 w-72 text-center shadow-2xl">
        <div className="w-10 h-10 bg-gray-700 rounded-xl flex items-center justify-center mx-auto mb-4">
          <Lock className="w-5 h-5 text-gray-300" />
        </div>
        <p className="font-bold text-white text-lg mb-1">관리자 확인</p>
        <p className="text-xs text-gray-500 mb-6">
          {error ? <span className="text-red-400">{error}</span> : 'PIN 번호를 입력하세요'}
        </p>
        <div className={`flex justify-center gap-4 mb-6 ${shake ? 'animate-bounce' : ''}`}>
          {[0,1,2,3].map(i => (
            <div
              key={i}
              className={`w-3.5 h-3.5 rounded-full transition-all duration-150 ${
                shake ? 'bg-red-500' : i < pin.length ? 'bg-primary-500 scale-110' : 'bg-gray-600'
              }`}
            />
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 mb-4">
          {[1,2,3,4,5,6,7,8,9].map(n => (
            <button key={n} {...tapHandler(() => press(String(n)))} disabled={loading}
              className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white text-lg font-bold rounded-xl py-3 transition-colors touch-manipulation"
            >
              {n}
            </button>
          ))}
          <div />
          <button {...tapHandler(() => press('0'))} disabled={loading}
            className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white text-lg font-bold rounded-xl py-3 transition-colors touch-manipulation"
          >
            0
          </button>
          <button {...tapHandler(del)} disabled={loading}
            className="bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white rounded-xl py-3 transition-colors flex items-center justify-center touch-manipulation"
          >
            <Delete className="w-4 h-4" />
          </button>
        </div>
        {onCancel && (
          <button onClick={onCancel} className="text-gray-500 text-sm hover:text-gray-300 transition-colors mt-1">
            취소
          </button>
        )}
      </div>
    </div>
  );
}

// ── 설정/종료 모달 ────────────────────────────────────────
function ExitModal({ onClose, onReset }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center backdrop-blur-sm">
      <div
        className="rounded-2xl p-8 w-80 text-center shadow-2xl"
        style={{ background: '#1a1a2e', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        <div className="w-10 h-10 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center mx-auto mb-4">
          <Settings className="w-5 h-5 text-gray-300" />
        </div>
        <p className="font-bold text-white text-lg mb-1">키오스크 설정</p>
        <p className="text-xs text-gray-500 mb-1">PIN 변경은 대시보드 → 설정에서 할 수 있습니다.</p>
        <p className="text-xs text-gray-600 mb-6">다른 기기에서 로그인 후 변경하세요.</p>
        <div className="space-y-2">
          <button
            onClick={() => { window.location.href = '/'; }}
            className="w-full py-3 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 transition-colors"
          >
            대시보드로 이동
          </button>
          <button
            onClick={onReset}
            className="w-full py-3 text-gray-400 rounded-xl transition-colors text-sm"
            style={{ border: '1px solid rgba(255,255,255,0.1)' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            헬스장 코드 재설정
          </button>
          <button
            onClick={onClose}
            className="w-full py-2 text-gray-600 text-sm hover:text-gray-400 transition-colors"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 결과 오버레이 ──────────────────────────────────────────
function ResultOverlay({ result }) {
  const isSuccess = result.status === 'success';
  const isWarning = result.status === 'warning';
  const Icon = isSuccess ? CheckCircle : isWarning ? AlertCircle : XCircle;

  const bg = isSuccess ? 'from-green-950 to-green-900' : isWarning ? 'from-yellow-950 to-yellow-900' : 'from-red-950 to-red-900';
  const iconColor = isSuccess ? 'text-green-400' : isWarning ? 'text-yellow-400' : 'text-red-400';
  const msgColor = isSuccess ? 'text-green-300' : isWarning ? 'text-yellow-300' : 'text-red-300';

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black/75 z-30 backdrop-blur-sm">
      <div className={`bg-gradient-to-b ${bg} rounded-3xl p-12 text-center max-w-sm w-full mx-6 shadow-2xl`}>
        <Icon className={`w-20 h-20 ${iconColor} mx-auto mb-6`} />
        {result.member_name && (
          <p className="text-5xl font-black text-white mb-4">{result.member_name}님</p>
        )}
        {result.message && (
          <p className={`text-xl font-semibold ${msgColor} leading-relaxed`}>{result.message}</p>
        )}
      </div>
    </div>
  );
}

// ── 메인 키오스크 ──────────────────────────────────────────
export default function KioskStandalone() {
  const [searchParams] = useSearchParams();
  const [gymCode, setGymCode] = useState(() => {
    const urlCode = searchParams.get('kiosk_code') || searchParams.get('code');
    if (urlCode) { localStorage.setItem(STORAGE_KEY, urlCode.toUpperCase()); return urlCode.toUpperCase(); }
    return localStorage.getItem(STORAGE_KEY) || '';
  });
  const [gymName, setGymName] = useState('');
  const [clock, setClock] = useState({ time: '', date: '' });
  const [phone, setPhone] = useState('');
  const [result, setResult] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [mode, setMode] = useState('idle'); // 'idle' | 'qr' | 'phone'
  const [showPinModal, setShowPinModal] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [lockOverlay, setLockOverlay] = useState(false);
  const [weather, setWeather] = useState(null);
  const [ranking, setRanking] = useState([]);

  const [isLandscape, setIsLandscape] = useState(() => window.innerWidth > window.innerHeight);

  const processingRef = useRef(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const lastScanRef = useRef(0);
  const resetTimerRef = useRef(null);

  // 가로/세로 감지
  useEffect(() => {
    function handleResize() { setIsLandscape(window.innerWidth > window.innerHeight); }
    window.addEventListener('resize', handleResize);
    // orientation change (모바일/태블릿)
    const orient = window.screen?.orientation;
    if (orient) {
      orient.addEventListener('change', handleResize);
    }
    return () => {
      window.removeEventListener('resize', handleResize);
      if (orient) {
        orient.removeEventListener('change', handleResize);
      }
    };
  }, []);

  // 시계
  useEffect(() => {
    const tick = () => setClock({
      time: format(new Date(), 'HH:mm', { locale: ko }),
      date: format(new Date(), 'M월 d일 (EEE)', { locale: ko }),
    });
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);

  // 헬스장명 로드
  useEffect(() => {
    if (!gymCode) return;
    supabase.rpc('kiosk_get_gym_name', { p_gym_code: gymCode })
      .then(({ data }) => { if (data) setGymName(data); });
  }, [gymCode]);

  // 날씨 캐싱 (최초 + 60분 간격)
  useEffect(() => {
    if (!gymCode) return;
    loadWeather().then(data => { if (data) setWeather(data); });
    const t = setInterval(() => loadWeather().then(data => { if (data) setWeather(data); }), WEATHER_TTL);
    return () => clearInterval(t);
  }, [gymCode]);

  // 출석왕 랭킹 (최초 + 5분 간격)
  useEffect(() => {
    if (!gymCode) return;
    async function fetchRanking() {
      try {
        const { data } = await supabase.rpc('kiosk_get_attendance_ranking', { p_gym_code: gymCode });
        if (Array.isArray(data)) setRanking(data);
      } catch { /* RPC 미설치 시 무시 */ }
    }
    fetchRanking();
    const t = setInterval(fetchRanking, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [gymCode]);

  // [P6/P11] 브라우저 뒤로가기 차단
  useEffect(() => {
    if (!gymCode) return;
    window.history.pushState({ kiosk: true }, '');
    function handlePopState() {
      window.history.pushState({ kiosk: true }, '');
      setLockOverlay(true);
    }
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [gymCode]);

  // 풀스크린 이탈 감지 (키오스크 잠금)
  useEffect(() => {
    if (!gymCode) return;

    function onFullscreenChange() {
      if (!document.fullscreenElement) {
        setLockOverlay(true);
      }
    }

    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, [gymCode]);

  // 카메라
  const startCamera = useCallback(async () => {
    setCameraError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setScanning(true);
      }
    } catch {
      setCameraError('카메라를 사용할 수 없습니다.');
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(t => t.stop());
      videoRef.current.srcObject = null;
    }
    setScanning(false);
  }, []);

  // 모드에 따라 카메라 on/off
  useEffect(() => {
    if (mode === 'qr' && gymCode) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [mode, gymCode]); // eslint-disable-line

  // QR 스캔 루프
  const scanLoop = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !scanning) return;
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      const now = Date.now();
      if (now - lastScanRef.current > SCAN_INTERVAL) {
        lastScanRef.current = now;
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0);
        const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
        if (code?.data?.startsWith('gym-member:')) {
          const memberId = code.data.replace('gym-member:', '');
          handleQRCheckin(memberId);
          return;
        }
      }
    }
    rafRef.current = requestAnimationFrame(scanLoop);
  }, [scanning]); // eslint-disable-line

  useEffect(() => {
    if (scanning && !cameraError && gymCode) {
      rafRef.current = requestAnimationFrame(scanLoop);
    }
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [scanning, scanLoop, cameraError, gymCode]);

  function showResult(data) {
    const isSuccess = data.status === 'success' && data.member_name;
    const cheer = CHEERS[Math.floor(Math.random() * CHEERS.length)];
    const finalData = isSuccess
      ? { ...data, message: buildCheckinMessage(data.member_name, weather, cheer) }
      : data;
    setResult(finalData);
    setScanning(false);
    setPhone('');
    processingRef.current = false;
    // 음성: 짧고 자연스럽게 (이름 한 번만)
    const speechText = isSuccess
      ? buildSpeechMessage(data.member_name, cheer)
      : finalData.message;
    speak(speechText);
    clearTimeout(resetTimerRef.current);
    resetTimerRef.current = setTimeout(() => {
      setResult(null);
      setMode('idle');
    }, RESET_DELAY);
  }

  async function handleQRCheckin(memberId) {
    if (processingRef.current) return;
    processingRef.current = true;
    setScanning(false);

    try {
      const { data, error } = await supabase.rpc('kiosk_checkin_by_qr', { p_gym_code: gymCode, p_member_id: memberId });
      if (error) throw error;
      showResult(data || { status: 'error', message: '오류가 발생했습니다.' });
    } catch (e) {
      console.error('QR 체크인 오류:', e);
      showResult({ status: 'error', message: '체크인 처리 중 오류가 발생했습니다.' });
    }
  }

  useEffect(() => {
    if (phone.length === 8 && !processingRef.current) {
      processingRef.current = true;
  
      const fullPhone = '010' + phone;
      supabase.rpc('kiosk_checkin_by_phone', { p_gym_code: gymCode, p_phone: fullPhone })
        .then(({ data, error }) => {
          if (error) throw error;
          showResult(data || { status: 'error', message: '오류가 발생했습니다.' });
        })
        .catch(e => {
          console.error('전화번호 체크인 오류:', e);
          showResult({ status: 'error', message: '체크인 처리 중 오류가 발생했습니다.' });
        });
    }
  }, [phone]); // eslint-disable-line

  function handleSetup(code, name) {
    localStorage.setItem(STORAGE_KEY, code);
    setGymCode(code);
    setGymName(name);
  }

  if (!gymCode) return <SetupScreen onSetup={handleSetup} />;

  // 풀스크린 이탈 잠금 오버레이
  if (lockOverlay) {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center" style={{ background: '#0a0a0f' }}>
        <div className="w-16 h-16 bg-primary-600 rounded-3xl flex items-center justify-center mb-6 shadow-lg shadow-primary-900/50">
          <Dumbbell className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-2xl font-black text-white mb-1">{gymName || 'GYM Manager'}</h2>
        <p className="text-gray-500 text-sm mb-10">키오스크가 잠겨 있습니다</p>
        <PinModal
          gymCode={gymCode}
          onSuccess={() => {
            setLockOverlay(false);
            // 풀스크린 재진입
            document.documentElement.requestFullscreen?.().catch(() => {});
          }}
          onCancel={null}
        />
      </div>
    );
  }

  // 세로 모드: 브랜딩 패널 (컴팩트)
  const brandingPanel = (
    <div className={`${isLandscape ? 'w-1/2 border-r border-white/5' : 'border-b border-white/5'} flex flex-col relative overflow-hidden`}>
      {/* 배경 장식 */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-900 to-black" />
      <div className="absolute -top-40 -left-40 w-[28rem] h-[28rem] bg-primary-600/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-20 w-96 h-96 bg-primary-900/15 rounded-full blur-3xl pointer-events-none" />

      <div className={`relative z-10 flex flex-col items-center h-full ${isLandscape ? 'py-10 px-10' : 'py-4 px-6'}`}>
        {/* 상단 — 로고 */}
        <div className={`flex items-center gap-2.5 self-start ${isLandscape ? '' : 'mb-2'}`}>
          <div className="w-9 h-9 bg-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-900/40">
            <Dumbbell className="w-4.5 h-4.5 text-white" style={{ width: '1.125rem', height: '1.125rem' }} />
          </div>
          <span className="text-gray-600 text-sm font-medium tracking-wide">GYM Manager</span>
        </div>

        {/* 중앙 — 헬스장명 + 시계 */}
        <div className={`flex-1 flex ${isLandscape ? 'flex-col items-center justify-center' : 'items-center gap-6'} text-center w-full`}>
          {isLandscape && (
            <p className="text-gray-500 text-sm font-medium uppercase tracking-[0.2em] mb-3">출석 체크</p>
          )}
          <h1 className={`${isLandscape ? 'text-5xl mb-10' : 'text-2xl'} font-black text-white leading-tight break-keep`}>
            {gymName || '헬스장'}
          </h1>

          <div
            className={`${isLandscape ? 'w-full rounded-3xl px-8 py-7 mb-4' : 'flex-1 rounded-2xl px-4 py-3'} text-center`}
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <p className={`${isLandscape ? 'text-7xl' : 'text-4xl'} font-bold text-white tabular-nums tracking-tight leading-none`}>
              {clock.time}
            </p>
            <p className={`text-gray-500 ${isLandscape ? 'text-sm mt-3' : 'text-xs mt-1'} font-medium`}>{clock.date}</p>
            {weather && (
              <p className={`text-gray-400 ${isLandscape ? 'text-sm mt-2' : 'text-xs mt-1'}`}>
                {weather.desc} · {weather.temp}°C
              </p>
            )}
          </div>

          {/* 출석왕 랭킹 — 가로 모드에서만 */}
          {isLandscape && ranking.length > 0 && (
            <div
              className="w-full rounded-2xl px-5 py-4"
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
            >
              <p className="text-xs text-gray-600 font-medium tracking-wider uppercase text-center mb-3">
                이달의 출석왕
              </p>
              <div className="space-y-2">
                {ranking.map((r, i) => {
                  const medals = ['🥇', '🥈', '🥉'];
                  return (
                    <div key={i} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-base">{medals[i]}</span>
                        <span className="text-gray-300 text-sm font-medium">
                          {maskName(r.member_name)}
                        </span>
                        <span className="text-gray-600 text-xs font-mono">
                          {maskPhone(r.member_phone)}
                        </span>
                      </div>
                      <span className="text-gray-500 text-xs">{r.count}회</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* 하단 — 광고 슬롯 (가로 모드에서만) */}
        {isLandscape && (
          <div
            className="w-full rounded-2xl px-5 py-4 text-center"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}
          >
            <p className="text-xs text-gray-700 mb-0.5 font-medium tracking-wider uppercase">Powered by</p>
            <p className="text-sm font-bold text-gray-600">GYM Manager</p>
            <p className="text-xs text-gray-700 mt-0.5">헬스장 통합 관리 솔루션</p>
          </div>
        )}
      </div>
    </div>
  );

  // 체크인 패널
  const checkinPanel = (
    <div className={`${isLandscape ? 'w-1/2' : 'flex-1'} flex flex-col`} style={{ background: '#0f0f17' }}>
      {/* 헤더 */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
        {mode !== 'idle' ? (
          <button
            onClick={() => setMode('idle')}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-200 transition-colors text-sm py-1 px-2 rounded-lg hover:bg-white/5 touch-manipulation"
          >
            <ArrowLeft className="w-4 h-4" />
            돌아가기
          </button>
        ) : <div />}
        <button
          onClick={() => setShowPinModal(true)}
          className="p-2 text-gray-700 hover:text-gray-400 transition-colors rounded-xl hover:bg-white/5 touch-manipulation"
          title="관리자 설정"
        >
          <Settings className="w-5 h-5" />
        </button>
      </div>

      {/* 모드: idle — 방법 선택 */}
      {mode === 'idle' && (
        <div className={`flex-1 flex ${isLandscape ? 'flex-col' : 'flex-row'} items-center justify-center px-8 gap-5`}>
          {isLandscape && (
            <div className="text-center mb-2">
              <p className="text-2xl font-bold text-white">체크인 방법 선택</p>
              <p className="text-gray-600 text-sm mt-1.5">원하는 방법을 선택해 주세요</p>
            </div>
          )}

          <button
            onClick={() => setMode('qr')}
            className={`${isLandscape ? 'w-full max-w-xs py-7' : 'flex-1 py-6'} rounded-2xl flex flex-col items-center gap-4 transition-all duration-150 active:scale-95 group touch-manipulation`}
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(220,38,38,0.4)'; e.currentTarget.style.background = 'rgba(220,38,38,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          >
            <div className={`${isLandscape ? 'w-16 h-16' : 'w-12 h-12'} bg-primary-600/15 rounded-2xl flex items-center justify-center group-hover:bg-primary-600/25 transition-colors`}>
              <QrCode className={`${isLandscape ? 'w-9 h-9' : 'w-7 h-7'} text-primary-400`} />
            </div>
            <div className="text-center">
              <p className={`text-white font-bold ${isLandscape ? 'text-xl' : 'text-lg'}`}>QR 코드</p>
              <p className={`text-gray-500 ${isLandscape ? 'text-sm' : 'text-xs'} mt-0.5`}>회원 앱의 QR 코드 스캔</p>
            </div>
          </button>

          <button
            onClick={() => setMode('phone')}
            className={`${isLandscape ? 'w-full max-w-xs py-7' : 'flex-1 py-6'} rounded-2xl flex flex-col items-center gap-4 transition-all duration-150 active:scale-95 group touch-manipulation`}
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(59,130,246,0.4)'; e.currentTarget.style.background = 'rgba(59,130,246,0.06)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; }}
          >
            <div className={`${isLandscape ? 'w-16 h-16' : 'w-12 h-12'} bg-blue-600/15 rounded-2xl flex items-center justify-center group-hover:bg-blue-600/25 transition-colors`}>
              <Phone className={`${isLandscape ? 'w-9 h-9' : 'w-7 h-7'} text-blue-400`} />
            </div>
            <div className="text-center">
              <p className={`text-white font-bold ${isLandscape ? 'text-xl' : 'text-lg'}`}>전화번호</p>
              <p className={`text-gray-500 ${isLandscape ? 'text-sm' : 'text-xs'} mt-0.5`}>뒷자리 8자리 입력</p>
            </div>
          </button>
        </div>
      )}

      {/* 모드: QR 스캔 */}
      {mode === 'qr' && (
        <div className="flex-1 relative overflow-hidden bg-black">
          {cameraError ? (
            <div className="flex items-center justify-center h-full text-center p-8">
              <div>
                <XCircle className="w-14 h-14 mx-auto mb-3 text-red-500" />
                <p className="text-white font-medium mb-1">카메라 오류</p>
                <p className="text-sm text-gray-400">{cameraError}</p>
              </div>
            </div>
          ) : (
            <>
              <video ref={videoRef} className="w-full h-full object-cover" muted playsInline />
              <canvas ref={canvasRef} className="hidden" />
              {scanning && (
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="absolute inset-0 bg-black/45" />
                  <div className={`relative ${isLandscape ? 'w-56 h-56' : 'w-48 h-48'}`}>
                    {[['top-0 left-0','border-t-[3px] border-l-[3px]'],
                      ['top-0 right-0','border-t-[3px] border-r-[3px]'],
                      ['bottom-0 left-0','border-b-[3px] border-l-[3px]'],
                      ['bottom-0 right-0','border-b-[3px] border-r-[3px]'],
                    ].map(([pos, border], i) => (
                      <div key={i} className={`absolute w-8 h-8 ${pos} ${border} border-primary-400 rounded-sm`} />
                    ))}
                    <div className="absolute inset-x-3 h-px bg-primary-400/50 animate-pulse" style={{ top: '50%' }} />
                  </div>
                  <p className="relative mt-5 text-white/60 text-sm font-medium tracking-widest uppercase">
                    QR 스캔
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* 모드: 전화번호 */}
      {mode === 'phone' && (
        <div className="flex-1 px-6 py-5 flex flex-col">
          <p className="text-gray-600 text-xs text-center mb-4 font-medium tracking-wider uppercase">
            전화번호 뒷자리 8자리 입력
          </p>
          <div className="flex-1">
            <NumPad value={phone} onChange={setPhone} />
          </div>
        </div>
      )}

      {/* 결과 오버레이 */}
      {result && <ResultOverlay result={result} />}
    </div>
  );

  return (
    <div className={`fixed inset-0 bg-gray-950 flex ${isLandscape ? 'flex-row' : 'flex-col'} overflow-hidden select-none`} style={{ background: '#0a0a0f' }}>
      {brandingPanel}
      {checkinPanel}

      {/* ── PIN 모달 ── */}
      {showPinModal && (
        <PinModal
          gymCode={gymCode}
          onSuccess={() => { setShowPinModal(false); setShowExitModal(true); }}
          onCancel={() => setShowPinModal(false)}
        />
      )}

      {/* ── 설정/종료 모달 ── */}
      {showExitModal && (
        <ExitModal
          onClose={() => setShowExitModal(false)}
          onReset={() => { localStorage.removeItem(STORAGE_KEY); setGymCode(''); setShowExitModal(false); }}
        />
      )}
    </div>
  );
}
